"use client";

import React, { createContext, useContext, useState, useEffect } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
type Theme = "dark" | "light";
type Lang = "en" | "ar";

interface AppContextType {
  theme: Theme;
  lang: Lang;
  toggleTheme: () => void;
  t: (key: string) => string;
  isRTL: boolean;
}

// ─── Translations ─────────────────────────────────────────────────────────────
const translations: Record<Lang, Record<string, string>> = {
  en: {
    // Navbar
    "nav.home": "Home",
    "nav.services": "Services",
    "nav.about": "About",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "nav.cta": "Get Started",
    // Hero
    "hero.badge": "Next-Generation IT Services",
    "hero.headline1": "Modern IT",
    "hero.headline2": "Infrastructure",
    "hero.headline3": "& Digital Services",
    "hero.subtitle": "We specialize in cutting-edge Web & Mobile Development, Cloud Hosting, IT Helpdesk, IT Infrastructure, and On-Demand Services — delivering seamless integration for modern businesses.",
    "hero.cta1": "Get Started Today →",
    "hero.cta2": "View Our Services",
    "hero.stat1.n": "200+",
    "hero.stat1.label": "Clients Worldwide",
    "hero.stat2.n": "99.9%",
    "hero.stat2.label": "Uptime SLA",
    "hero.stat3.n": "10+",
    "hero.stat3.label": "Years Experience",
    "hero.card.title": "Enterprise IT Solutions",
    "hero.card.desc": "Tailored infrastructure and software solutions that scale with your business needs.",
    // Services
    "services.badge": "What We Do",
    "services.title1": "Our",
    "services.title2": "Services",
    "services.subtitle": "Everything your business needs to thrive in the digital era — from development to deployment and beyond.",
    "services.learn": "Learn more →",
    "services.web.title": "Web Development",
    "services.web.desc": "Full-stack web applications built with cutting-edge frameworks. Responsive, performant, and scalable.",
    "services.mobile.title": "Mobile Development",
    "services.mobile.desc": "Native and cross-platform mobile apps for iOS and Android that deliver outstanding user experiences.",
    "services.hosting.title": "Cloud Hosting",
    "services.hosting.desc": "Enterprise-grade hosting solutions with 99.9% uptime SLA, full redundancy, and 24/7 monitoring.",
    "services.helpdesk.title": "IT Helpdesk",
    "services.helpdesk.desc": "Expert technical support and helpdesk services to keep your team productive and systems running smoothly.",
    "services.infra.title": "IT Infrastructure",
    "services.infra.desc": "End-to-end infrastructure design, deployment, and management for enterprise environments.",
    "services.ondemand.title": "On-Demand Services",
    "services.ondemand.desc": "Flexible, scalable IT resources available on-demand. Scale up or down based on your real-time needs.",
    // WhyUs
    "why.badge": "Why Choose Us",
    "why.title1": "Trusted by businesses",
    "why.title2": "around the world",
    "why.subtitle": "With over a decade of experience, Qumrahsoft delivers enterprise-grade technology solutions that help organizations transform, grow, and succeed in competitive markets.",
    "why.f1.title": "Lightning-Fast Delivery",
    "why.f1.desc": "Agile processes that deliver results on time, every time.",
    "why.f2.title": "Enterprise Security",
    "why.f2.desc": "Bank-grade security and compliance built into everything we build.",
    "why.f3.title": "Scalable Solutions",
    "why.f3.desc": "Infrastructure that grows seamlessly with your business.",
    // CTA
    "cta.badge": "Ready to Transform?",
    "cta.title": "Start Your Digital Transformation Today",
    "cta.subtitle": "Join 200+ organizations that trust Qumrahsoft to power their technology. Let's build something extraordinary together.",
    "cta.btn1": "Schedule a Free Consultation",
    "cta.btn2": "View Case Studies",
    // Footer
    "footer.desc": "Delivering next-generation technology solutions that empower businesses to innovate and scale.",
    "footer.services": "Services",
    "footer.company": "Company",
    "footer.contact": "Contact",
    "footer.rights": "© 2024 QUMRAHSOFT. All rights reserved.",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.cookies": "Cookie Policy",
  },
  ar: {
    // Navbar
    "nav.home": "الرئيسية",
    "nav.services": "الخدمات",
    "nav.about": "من نحن",
    "nav.blog": "المدونة",
    "nav.contact": "تواصل معنا",
    "nav.cta": "ابدأ الآن",
    // Hero
    "hero.badge": "خدمات تقنية المعلومات الجيل القادم",
    "hero.headline1": "بنية تحتية",
    "hero.headline2": "تقنية متطورة",
    "hero.headline3": "وخدمات رقمية",
    "hero.subtitle": "نتخصص في تطوير الويب والجوال المتطور، الاستضافة السحابية، دعم تقنية المعلومات، البنية التحتية والخدمات عند الطلب — نقدم تكاملًا سلسًا للشركات الحديثة.",
    "hero.cta1": "ابدأ اليوم ←",
    "hero.cta2": "عرض خدماتنا",
    "hero.stat1.n": "+200",
    "hero.stat1.label": "عميل حول العالم",
    "hero.stat2.n": "٩٩.٩٪",
    "hero.stat2.label": "ضمان وقت التشغيل",
    "hero.stat3.n": "+10",
    "hero.stat3.label": "سنوات خبرة",
    "hero.card.title": "حلول تقنية للمؤسسات",
    "hero.card.desc": "حلول بنية تحتية وبرمجيات مخصصة تتوسع مع احتياجات عملك.",
    // Services
    "services.badge": "ماذا نفعل",
    "services.title1": "خدماتنا",
    "services.title2": "",
    "services.subtitle": "كل ما يحتاجه عملك للنجاح في العصر الرقمي — من التطوير حتى النشر وما بعده.",
    "services.learn": "اعرف المزيد ←",
    "services.web.title": "تطوير الويب",
    "services.web.desc": "تطبيقات ويب متكاملة مبنية بأحدث الأطر. متجاوبة وعالية الأداء وقابلة للتوسع.",
    "services.mobile.title": "تطوير التطبيقات",
    "services.mobile.desc": "تطبيقات موبايل أصلية ومتعددة المنصات لـ iOS وAndroid تقدم تجارب مستخدم استثنائية.",
    "services.hosting.title": "الاستضافة السحابية",
    "services.hosting.desc": "حلول استضافة بمستوى المؤسسات مع ضمان٩٩.٩٪ تشغيل متواصل وحماية كاملة.",
    "services.helpdesk.title": "الدعم التقني",
    "services.helpdesk.desc": "خدمات دعم تقني متخصصة تبقي فريقك منتجًا وأنظمتك تعمل بسلاسة.",
    "services.infra.title": "البنية التحتية",
    "services.infra.desc": "تصميم ونشر وإدارة شاملة للبنية التحتية لبيئات المؤسسات.",
    "services.ondemand.title": "الخدمات عند الطلب",
    "services.ondemand.desc": "موارد تقنية مرنة وقابلة للتطوير متاحة عند الطلب حسب احتياجاتك الفعلية.",
    // WhyUs
    "why.badge": "لماذا تختارنا",
    "why.title1": "موثوق به من الشركات",
    "why.title2": "حول العالم",
    "why.subtitle": "بخبرة تمتد لأكثر من عقد، تقدم كيومراسوفت حلولًا تقنية بمستوى المؤسسات تساعد المنظمات على التحول والنمو والنجاح.",
    "why.f1.title": "تسليم فائق السرعة",
    "why.f1.desc": "عمليات رشيقة تحقق النتائج في الوقت المحدد، في كل مرة.",
    "why.f2.title": "أمان على مستوى المؤسسات",
    "why.f2.desc": "أمان وامتثال على مستوى البنوك مدمج في كل ما نبنيه.",
    "why.f3.title": "حلول قابلة للتوسع",
    "why.f3.desc": "بنية تحتية تنمو بسلاسة مع عملك.",
    // CTA
    "cta.badge": "هل أنت مستعد للتحول؟",
    "cta.title": "ابدأ تحولك الرقمي اليوم",
    "cta.subtitle": "انضم إلى أكثر من ٢٠٠ منظمة تثق بكيومراسوفت لتشغيل تقنيتها. لنبني شيئًا استثنائيًا معًا.",
    "cta.btn1": "جدّل استشارة مجانية",
    "cta.btn2": "عرض دراسات الحالة",
    // Footer
    "footer.desc": "تقديم حلول تقنية الجيل القادم التي تمكّن الشركات من الابتكار والتوسع.",
    "footer.services": "الخدمات",
    "footer.company": "الشركة",
    "footer.contact": "تواصل معنا",
    "footer.rights": "© ٢٠٢٤ كيومراسوفت. جميع الحقوق محفوظة.",
    "footer.privacy": "سياسة الخصوصية",
    "footer.terms": "شروط الخدمة",
    "footer.cookies": "سياسة ملفات تعريف الارتباط",
  },
};

// ─── Context ──────────────────────────────────────────────────────────────────
const AppContext = createContext<Omit<AppContextType, 'setLang'>>({
  theme: "dark",
  lang: "en",
  toggleTheme: () => {},
  t: (k) => k,
  isRTL: false,
});

export function AppProvider({ children, initialLang }: { children: React.ReactNode, initialLang: Lang }) {
  const [theme, setTheme] = useState<Theme>("dark");
  const lang = initialLang;

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const t = (key: string) => translations[lang][key] ?? key;

  const isRTL = lang === "ar";

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", lang);
  }, [theme, lang, isRTL]);

  return (
    <AppContext.Provider value={{ theme, lang, toggleTheme, t, isRTL }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
